import {useEffect, useRef, useState} from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Space,
  Spin,
  Steps,
  Tag,
  Typography,
  notification,
  Popconfirm,
} from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  EditOutlined,
  SaveOutlined,
  TagOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import type {NewProgram} from '@PikaDoApp/npm-pikado-logic/dist/modules/program/graphql/program.interface';
import {PackagesService} from './PackagesService';
import {ProgramsNewService} from '../Program/ProgramsNewService';
import {PackageFormFields, COUNTRIES} from './components/PackageFormFields';
import {getChangedFields} from '../../utils/unchagedFields.package';
import type {
  NewPackageById,
  NewUpdatePackageInput,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/package/graphql/package.interface';

const {Title, Text} = Typography;

const packagesService = new PackagesService();
const programService = new ProgramsNewService();

const STEPS = [
  {title: 'Info', icon: <TagOutlined />},
  {title: 'Details', icon: <AppstoreOutlined />},
  {title: 'Pricing', icon: <DollarOutlined />},
  {title: 'Review', icon: <CheckCircleOutlined />},
];

// ── Helpers ──────────────────────────────────────────────────────

/** Maps a NewPackageById to flat form values antd Form understands */
const packageToFormValues = (pkg: NewPackageById) => ({
  name_i18n: pkg.name_i18n ?? {},
  description_i18n: pkg.description_i18n ?? {},
  benefits_i18n: pkg.benefits_i18n ?? {},
  programId: pkg.programId,
  packageDuration: pkg.packageDuration,
  type: pkg.type ?? 'STUDENT',
  productIAP: pkg.productIAP ?? '',
  onlyAI: pkg.onlyAI ?? false,
  isFree: pkg.isFree ?? false,
  isActive: pkg.isActive ?? false,
  price: pkg.price ?? {},
});

const cleanI18n = (obj?: {en?: string; ar?: string; tr?: string}) => {
  if (!obj) return undefined;
  const result: any = {};
  if (obj.en?.trim()) result.en = obj.en.trim();
  if (obj.ar?.trim()) result.ar = obj.ar.trim();
  if (obj.tr?.trim()) result.tr = obj.tr.trim();
  return Object.keys(result).length > 0 ? result : undefined;
};

const cleanPrice = (priceObj?: any) => {
  if (!priceObj) return undefined;
  const result: any = {};
  for (const [country, data] of Object.entries(priceObj)) {
    if ((data as any)?.price > 0) {
      result[country] = {price: Number((data as any).price)};
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
};

// ── Component ────────────────────────────────────────────────────
export const PackageEdit = () => {
  const [form] = Form.useForm();
  const history = useHistory();
  const location = useLocation<{package?: NewPackageById}>();
  const {packageId} = useParams<{packageId: string}>();

  const [programs, setPrograms] = useState<NewProgram[]>([]);
  const [originalPkg, setOriginalPkg] = useState<NewPackageById | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [changedCount, setChangedCount] = useState(0);

  // Track original form values for diff
  const originalFormValues = useRef<any>(null);

  // ── Load data ──
  useEffect(() => {
    programService
      .getNewPrograms()
      .then(resp => setPrograms(resp))
      .catch(() => notification.error({message: 'Failed to load programs'}));

    const fromState = location.state?.package;
    if (fromState) {
      loadPackage(fromState);
    } else {
      packagesService
        .getPackageById(packageId)
        .then((pkg: any) => loadPackage(pkg))
        .catch(() => {
          setIsError(true);
          setIsFetching(false);
        });
    }
  }, [packageId]);

  const loadPackage = (pkg: NewPackageById) => {
    setOriginalPkg(pkg);
    const values = packageToFormValues(pkg);
    originalFormValues.current = values;
    form.setFieldsValue(values);
    setIsFetching(false);
  };

  // ── Track changed fields count live ──
  const onValuesChange = (_: any, all: any) => {
    if (!originalFormValues.current) return;
    const diff = getChangedFields(originalFormValues.current, all);
    setChangedCount(Object.keys(diff).length);

    // Auto-advance steps
    if (all.name_i18n?.en && currentStep === 0) setCurrentStep(1);
    if (all.programId && currentStep === 1) setCurrentStep(2);
    if (all.price?.US?.price > 0 && currentStep === 2) setCurrentStep(3);
  };

  // ── Submit — only send changed fields ──
  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const rawDiff = getChangedFields(originalFormValues.current, values);

      // Nothing changed
      if (Object.keys(rawDiff).length === 0) {
        notification.info({message: 'No changes detected', duration: 3});
        setIsSubmitting(false);
        return;
      }

      // Build clean input — only changed fields
      const changedInput: any = {id: originalPkg!._id};

      if (rawDiff.name_i18n)
        changedInput.name_i18n = cleanI18n(values.name_i18n);
      if (rawDiff.description_i18n)
        changedInput.description_i18n = cleanI18n(values.description_i18n);
      if (rawDiff.benefits_i18n)
        changedInput.benefits_i18n = cleanI18n(values.benefits_i18n);
      if (rawDiff.programId !== undefined)
        changedInput.programId = values.programId;
      if (rawDiff.packageDuration !== undefined)
        changedInput.packageDuration = values.packageDuration;
      if (rawDiff.type !== undefined) changedInput.type = values.type;
      if (rawDiff.onlyAI !== undefined) changedInput.onlyAI = values.onlyAI;
      if (rawDiff.isFree !== undefined) changedInput.isFree = values.isFree;
      if (rawDiff.isActive !== undefined)
        changedInput.isActive = values.isActive;
      if (rawDiff.productIAP !== undefined)
        changedInput.productIAP = values.productIAP || null;
      if (rawDiff.price) changedInput.price = cleanPrice(values.price);

      const updateInput: NewUpdatePackageInput = {
        id: originalPkg!._id,
        input: changedInput,
      };

      await packagesService.updatePackage(updateInput as any);

      notification.success({
        message: 'Package Updated!',
        description: `"${values.name_i18n?.en}" has been updated successfully.`,
        duration: 4,
      });

      setTimeout(() => history.push('/packages'), 1200);
    } catch (err: any) {
      if (err?.errorFields) return; // antd inline validation
      notification.error({
        message: 'Update Failed',
        description: err?.message || 'An unexpected error occurred.',
        duration: 6,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── States ──
  if (isError) {
    return (
      <div style={{padding: 40}}>
        <Alert
          type="error"
          message="Failed to load package"
          description="Please go back and try again."
          action={<Button onClick={() => history.goBack()}>Go Back</Button>}
        />
      </div>
    );
  }

  if (isFetching) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: 80}}>
        <Spin size="large" tip="Loading package..." />
      </div>
    );
  }

  const formValues = form.getFieldsValue(true);

  return (
    <div style={{padding: '24px 32px', maxWidth: 960, margin: '0 auto'}}>
      <Title level={2} style={{textAlign: 'center', marginBottom: 32}}>
        <EditOutlined style={{marginRight: 8}} />
        Edit Package
      </Title>

      {/* Changed fields indicator */}
      {changedCount > 0 && (
        <Alert
          type="info"
          message={
            <Text>
              You have{' '}
              <Text strong>
                {changedCount} unsaved change{changedCount > 1 ? 's' : ''}
              </Text>
            </Text>
          }
          style={{marginBottom: 16}}
          showIcon
        />
      )}

      <Steps
        current={currentStep}
        items={STEPS}
        style={{marginBottom: 32}}
        onChange={setCurrentStep}
      />

      <Form form={form} layout="vertical" onValuesChange={onValuesChange}>
        {/* Shared form fields (same as Create) */}
        <PackageFormFields
          programs={programs}
          formValues={formValues}
          showReview={false}
        />

        {/* ── Diff Summary (Edit-specific) ── */}
        <Card
          title={
            <>
              <CheckCircleOutlined /> &nbsp; Changes Summary
            </>
          }
          style={{marginBottom: 24}}>
          {changedCount === 0 ? (
            <Text type="secondary">No changes made yet.</Text>
          ) : (
            <Row gutter={[16, 8]}>
              {getChangedFields(originalFormValues.current ?? {}, formValues) &&
                Object.entries(
                  getChangedFields(
                    originalFormValues.current ?? {},
                    formValues,
                  ),
                ).map(([key, val]) => (
                  <Col span={12} key={key}>
                    <Text type="secondary" style={{fontSize: 12}}>
                      {key}
                    </Text>
                    <br />
                    <Tag color="blue" style={{marginTop: 2}}>
                      {typeof val === 'object' ? '(updated)' : String(val)}
                    </Tag>
                  </Col>
                ))}
            </Row>
          )}
        </Card>

        {/* Actions */}
        <div style={{textAlign: 'center'}}>
          <Space>
            <Popconfirm
              title="Are you sure you want to delete this package?"
              onConfirm={async () => {
                setIsSubmitting(true);
                try {
                  await packagesService.deletePackage(packageId);
                  notification.success({
                    message: 'Package deleted successfully',
                  });
                  history.push('/packages');
                } catch (err: any) {
                  notification.error({
                    message: 'Failed to delete package',
                    description: err?.message,
                  });
                  setIsSubmitting(false);
                }
              }}>
              <Button size="large" danger icon={<DeleteOutlined />}>
                Delete
              </Button>
            </Popconfirm>

            <Button size="large" onClick={() => history.goBack()}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="large"
              icon={<SaveOutlined />}
              loading={isSubmitting}
              disabled={changedCount === 0}
              onClick={handleUpdate}
              style={{minWidth: 180}}>
              Save Changes {changedCount > 0 && `(${changedCount})`}
            </Button>
          </Space>
        </div>
      </Form>
    </div>
  );
};
